<template>
  <div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("common.name") }}</label>
        <input
          type="text"
          v-model="localData.name"
          :placeholder="$t('common.name')"
          :class="{ 'field-error': vData.name && vData.name.$error }"
          class="form-control"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-sm-12 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.products.type") }}</label>
        <v-select
          v-model="localData.product_type"
          :options="productTypeList"
          :reduce="(option) => option"
          :class="{ 'field-error': vData.product_type && vData.product_type.$error }"
          :placeholder="$t('stock_management.products.select_product_type')"
          label="name"
          taggable
          autocomplete="off"
          @input="onProductTypeSelected"
          class="custom-select-dropdown-height"
          :disabled="!localData.is_editable && !!localData.product_type"
        ></v-select>
      </div>
      <div class="col-sm-12 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.products.select_mu") }}</label>
        <v-select
          v-model="localData.measure_unit"
          :reduce="(option) => option"
          :class="{ 'field-error': vData.measure_unit_id && vData.measure_unit_id.$error }"
          :placeholder="$t('stock_management.products.select_mu')"
          label="name"
          autocomplete="off"
          :options="measureUnitList"
          @input="onMeasureUnitSelected"
          class="custom-select-dropdown-height"
          :disabled="!localData.is_editable && !!localData.measure_unit_id"
        ></v-select>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("stock_management.products.code") }}</label>
        <input v-model="localData.code" class="form-control" placeholder="" />
      </div>
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("stock_management.products.barcode") }}</label>
        <input type="text" v-model="localData.barcode" :placeholder="$t('stock_management.products.barcode')" class="form-control" />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label mb-2">{{ $t("stock_management.products.lot_management") }}</label>
        <Toggle
          :is-active="isLotManagementActive"
          :on-change="onLotManagementChanged"
          :has-toggle-text="false"
          :disabled="!localData.is_editable"
        />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label mb-2">{{ $t("common.active") }}</label>
        <Toggle
          :is-active="isActive"
          :on-change="onIsActiveChanged"
          :has-toggle-text="false"
        />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("stock_management.products.base_product") }}</label>
        <div class="sm-select-input-dialog-btn" @click="fitoFertilitySelectClick">
          <div class="sm-select-input-dialog-info">
            <div class="sm-select-input-dialog-value">{{ localData?.fito_fertility_product_name ?? " - " }}</div>
          </div>
          <div class="sm-select-input-dialog-icon">
            <i class="fa-regular fa-edit"></i>
          </div>
        </div>
      </div>
    </div>
    <FitoFertilityProductSelector
      ref="fitoFertilityProductSelector"
      v-if="showProductSelectDialog"
      :visible="showProductSelectDialog"
      @close="showProductSelectDialog = false"
      @continue="onContinueFromProductSelector"
      :multiselect="false"
      :productUuid="localData.uuid"
    />
  </div>
</template>

<script>
import vSelect from "vue-select";
import Toggle from "@/components/Toggle.vue";
import FitoFertilityProductSelector from "@/components/FitoFertilityProductSelector.vue";

export default {
  name: "SMProductsFormStep",
  components: {
    vSelect,
    Toggle,
    FitoFertilityProductSelector,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    validations: {
      type: Object,
      default: null,
    },
    productTypes: {
      type: Array,
      default: () => [],
    },
    measureUnits: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localData: { ...this.value },
      showProductSelectDialog: false,
    };
  },
  computed: {
    vData() {
      return this.validations && this.validations.data ? this.validations.data : {};
    },
    productTypeList() {
      if (Array.isArray(this.productTypes)) {
        return this.productTypes.map((item) => ({
          value: item.uuid,
          name: item.name,
        }));
      }
      return [];
    },
    measureUnitList() {
      if (Array.isArray(this.measureUnits)) {
        return this.measureUnits.map((item) => ({
          value: item.id,
          name: `${item.name} (${item.symbol})`,
        }));
      }
      return [];
    },
    isLotManagementActive() {
      return Boolean(this.localData?.lot_management);
    },
    isActive() {
      return this.localData?.is_active !== 0;
    },
  },
  methods: {
    onProductTypeSelected(data) {
      if (data) {
        this.localData.product_type = {
          uuid: data?.value ?? null,
          name: data.name,
        };
      } else {
        this.localData.product_type = null;
      }
    },
    onMeasureUnitSelected(data) {
      if (data) {
        this.localData.measure_unit = {
          id: data.value,
          name: data.name,
        };
        this.localData.measure_unit_id = data.value;
      } else {
        this.localData.measure_unit = null;
        this.localData.measure_unit_id = null;
      }
    },
    fitoFertilitySelectClick() {
      this.showProductSelectDialog = true;
    },
    onContinueFromProductSelector(selectedProducts) {
      this.showProductSelectDialog = false;
      if (selectedProducts && selectedProducts.length > 0) {
        this.localData.fito_fertility_product_name = selectedProducts[0].name;
        this.localData.fito_fertility_product_uuid = selectedProducts[0].uuid;
      }
    },
    onLotManagementChanged(isActive) {
      this.localData.lot_management = isActive ? 1 : 0;
    },
    onIsActiveChanged(isActive) {
      this.localData.is_active = isActive ? 1 : 0;
    },
    validate() {
      if (this.vData && typeof this.vData.$touch === "function") {
        this.vData.$touch();
      }
      if (this.vData && this.vData.$invalid) {
        return [this.$t("fill_out_error")];
      }
      return [];
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (JSON.stringify(this.localData) !== JSON.stringify(newVal)) {
          this.localData = { ...newVal };
        }
      },
    },
    localData: {
      deep: true,
      handler(newVal) {
        this.$emit("input", newVal);
      },
    },
  },
};
</script>

<style scoped>
.custom-select-dropdown-height >>> .vs__dropdown-menu {
  max-height: 200px;
  overflow-y: auto;
}
</style>
